import { createSlice } from '@reduxjs/toolkit';
import { createAsyncThunkWrapper } from 'redux/thunk/createAsyncThunkWrapper';
import { getBasketData } from 'services/getBasketData';
import { FetchSuspendBasketPayload, SuspendBasketState } from 'types/baskets';
import { selectSelectedParishId } from 'redux/selectors/parishContext';

type SuspendState = SuspendBasketState & { loadedParishId: string | null };

const initialState: SuspendState = {
    suspendRecords: [],
    loading: false,
    error: null,
    loadMore: false,
    loadedParishId: null,
};

export const fetchSuspendBasket = createAsyncThunkWrapper<any, FetchSuspendBasketPayload>(
    'suspend/fetchSuspendBasket',
    async ({ status, subType, headers, name, companyId, parishId }: FetchSuspendBasketPayload, { getState }) => {
        const { pageSize, pageNumber } = headers;
        const resolvedParishId = parishId || companyId || selectSelectedParishId(getState() as any);
        if (!resolvedParishId) {
            return;
        }
        const res = await getBasketData({
            status,
            subType,
            parishId: resolvedParishId,
            headers,
            name,
        });
        return { response: res, pageNumber, pageSize, parishId: resolvedParishId };
    }
);

const suspendBasketSlice = createSlice({
    name: 'suspendBasket',
    initialState,
    reducers: {
        resetSuspendBaskets: () => ({ ...initialState }),
    },
    extraReducers: (builder) => {
        builder.addCase(fetchSuspendBasket.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(fetchSuspendBasket.fulfilled, (state, action) => {
            state.loading = false;
            if (!action?.payload) return;
            const { response, pageNumber, pageSize, parishId } = action.payload || {};
            const { events, loadMore, total } = response || {};
            if (pageNumber !== 0 && state.loadedParishId !== parishId) return;

            if (typeof loadMore === 'boolean') {
                state.loadMore = loadMore;
            } else {
                state.loadMore = typeof total === 'number' ? (pageNumber + 1) * pageSize < total : events?.length >= pageSize;
            }
            if (pageNumber === 0) {
                state.loadedParishId = parishId;
                state.suspendRecords = events || [];
            } else {
                state.suspendRecords = [...state.suspendRecords, ...(events || [])];
            }
        });
        builder.addCase(fetchSuspendBasket.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error.message || null;
        });
    },
});

export const { resetSuspendBaskets } = suspendBasketSlice.actions;
export default suspendBasketSlice.reducer;
